import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '../notification/notification.service';

interface BorisKnowledge {
  id: number;
  text: string;
  createdAt: string;
  updatedAt: string | null;
  createdBy: string | null;
}

interface BorisJob {
  id: number;
  status: 'pending' | 'running' | 'done' | 'error';
  requestedBy: string | null;
  createdAt: string;
  finishedAt: string | null;
  summary: string | null;
  error: string | null;
}

/** Tervezési feladat (Farmytól) — a Boris oldalon sorba rendezhető. */
interface BorisTask {
  id: number;
  from: string;
  instruction: string;
  status: 'pending' | 'running' | 'done' | 'error';
  requestedBy: string | null;
  conversationId: number;
  result: string | null;
  files: string[];
  createdAt: string;
  finishedAt: string | null;
}

interface BorisOverview {
  botUserId: number | null;
  enabled: boolean;
  workerOnline: boolean;
  workerSeenAt: string | null;
  workerInfo: string | null;
  knowledge: BorisKnowledge[];
  proposals: { id: number; text: string; createdAt: string }[];
  jobs: BorisJob[];
}

/** Boris 🤓 oldala: a tudástára (amit mindig figyelembe vesz), a javaslatai, a tervezőgép állapota. */
@Component({
  selector: 'app-boris',
  templateUrl: './boris.component.html',
  styleUrl: './boris.component.scss',
})
export class BorisComponent implements OnInit, OnDestroy {
  loading = true;
  loadFailed = false;
  saving = false;
  data: BorisOverview | null = null;
  tasks: BorisTask[] = [];
  draft = '';
  editing: Record<number, string> = {};
  expanded: Record<number, boolean> = {};

  readonly examples = [
    'Két terráriumunk van: kicsi (… × … mm) és nagy (… × … mm) hátfallal.',
    'A mágnes 6×2 mm-es; a furat átmérője 6,2 mm, mélysége 2,2 mm.',
    'Nyomtató: Bambu Lab …, 0,4 mm fúvóka, PLA.',
    'Nincs 1,2 mm-nél vékonyabb fal vagy nyúlvány — eltörik.',
  ];

  readonly statusLabel: Record<BorisJob['status'], string> = { pending: 'Vár', running: 'Dolgozik', done: 'Kész', error: 'Hiba' };
  readonly taskStatusLabel: Record<BorisTask['status'], string> = { pending: 'Sorban', running: 'Tervezi', done: 'Átadva', error: 'Törölve' };

  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(private http: HttpClient, private notification: NotificationService) {}

  ngOnInit(): void {
    this.load();
    this.timer = setInterval(() => this.load(true), 30_000);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  load(silent = false): void {
    if (!silent) this.loading = true;
    this.http.get<BorisTask[]>('boris/tasks').subscribe({ next: (tasks) => (this.tasks = tasks), error: () => undefined });
    this.http.get<BorisOverview>('boris/overview').subscribe({
      next: (data) => this.apply(data),
      error: () => {
        this.loading = false;
        this.loadFailed = !this.data;
      },
    });
  }

  private apply(data: BorisOverview): void {
    this.data = data;
    this.loading = false;
    this.loadFailed = false;
    this.saving = false;
  }

  private fail(e: any, fallback: string): void {
    this.saving = false;
    this.notification.show(e?.error?.message || fallback, 'danger');
  }

  get chatLink(): string | null {
    return this.data?.botUserId ? `/chat/u-${this.data.botUserId}` : null;
  }

  get workerLabel(): string {
    if (!this.data) return '';
    if (!this.data.enabled) return 'Kikapcsolva';
    return this.data.workerOnline ? 'A tervezőgép fut' : 'A tervezőgép nem elérhető';
  }

  get openTasks(): BorisTask[] {
    return this.tasks.filter((t) => t.status === 'pending' || t.status === 'running');
  }

  get closedTasks(): BorisTask[] {
    return this.tasks.filter((t) => t.status !== 'pending' && t.status !== 'running');
  }

  /** Boris beszélgetése (a feladat a kérő és Boris privát beszélgetésében fut — a kérőnek ez a link oda visz) */
  get taskChatLink(): string | null {
    return this.chatLink;
  }

  moveTask(t: BorisTask, direction: 'up' | 'down'): void {
    this.http.post<BorisTask[]>(`boris/tasks/${t.id}/move`, { direction }).subscribe({
      next: (tasks) => (this.tasks = tasks),
      error: (e) => this.fail(e, 'Nem sikerült átrendezni.'),
    });
  }

  cancelTask(t: BorisTask): void {
    if (!confirm(`Törlöd a #T-${t.id} tervezési feladatot a sorból?`)) return;
    this.http.delete<BorisTask[]>(`boris/tasks/${t.id}`).subscribe({
      next: (tasks) => (this.tasks = tasks),
      error: (e) => this.fail(e, 'Nem sikerült törölni.'),
    });
  }

  toggleEnabled(): void {
    if (!this.data) return;
    this.http.patch<BorisOverview>('boris/settings', { enabled: !this.data.enabled }).subscribe({
      next: (data) => this.apply(data),
      error: (e) => this.fail(e, 'Nem sikerült módosítani.'),
    });
  }

  add(): void {
    const text = this.draft.trim();
    if (!text || this.saving) return;
    this.saving = true;
    this.http.post<BorisOverview>('boris/knowledge', { text }).subscribe({
      next: (data) => {
        this.draft = '';
        this.apply(data);
        this.notification.show('Boris megjegyezte. ✅', 'success');
      },
      error: (e) => this.fail(e, 'Nem sikerült menteni.'),
    });
  }

  useExample(text: string): void {
    this.draft = this.draft.trim() ? `${this.draft.trim()}\n${text}` : text;
  }

  startEdit(k: { id: number; text: string }): void {
    this.editing[k.id] = k.text;
  }

  cancelEdit(id: number): void {
    delete this.editing[id];
  }

  save(id: number, approve = false): void {
    const text = (this.editing[id] ?? '').trim();
    const body: { text?: string; approve?: boolean } = approve ? { approve: true } : {};
    if (this.editing[id] !== undefined) {
      if (!text) return;
      body.text = text;
    }
    this.saving = true;
    this.http.patch<BorisOverview>(`boris/knowledge/${id}`, body).subscribe({
      next: (data) => {
        delete this.editing[id];
        this.apply(data);
      },
      error: (e) => this.fail(e, 'Nem sikerült menteni.'),
    });
  }

  remove(id: number, question = 'Törlöd ezt a bejegyzést Boris tudástárából?'): void {
    if (!confirm(question)) return;
    this.http.delete<BorisOverview>(`boris/knowledge/${id}`).subscribe({
      next: (data) => this.apply(data),
      error: (e) => this.fail(e, 'Nem sikerült törölni.'),
    });
  }
}
